/**
 * Graph2 — orchestrátor. Spojí interaction state, data layer, render.
 *
 * Strukturální split (viz docs/plans/CHART-DATA-REFACTOR.md):
 *   - Graph2Interaction.ts : bounds + pan/zoom/touch handlers (hook)
 *   - Graph2Data.ts        : useChartData + indexed maps + minMax (hook)
 *   - Graph2Render.tsx     : Recharts ComposedChart (pure render)
 *   - Graph2Tooltip.tsx    : tooltip (pure render, mimo komponent)
 *
 * Tento soubor je < 100 řádků — žádný state, žádný fetch, žádné side effects
 * mimo auto-scroll efekt (bounds → latest data).
 */

import React, { useEffect, useMemo } from "react";
import { useInView } from "@client/hooks/useInView";
import { useValuesStore } from "@client/store/values";
import { useOptionalHidingWrapper } from "@client/components/wrappers/HidingWrapper";
import { useOptionalPartialWrapper } from "@client/components/wrappers/PartialWrapper";
import { useGraph2Data } from "./Graph2Data";
import { useGraph2Interaction, DEFAULT_BOUND } from "./Graph2Interaction";
import Graph2Render from "./Graph2Render";

interface Graph2Props {
  topicIds: string[];
  hidden?: boolean[];
  style?: React.CSSProperties;
  /** Pokud je všech topicIds boolean, renderovat jako stepBefore line. Currently
      Graph2Render čte `valueType` z topic metadata per topic — tato prop je
      pro backward compat (ignorováno, derive z metadata). */
  boolean?: boolean;
  className?: string;
  isFullscreen?: boolean;
  /**
   * Override pro visibility-aware fetching (default: true × inView × wrapperVisible).
   * Předáním `enabled={false}` lze fetch tvrdě pausnout (např. v testu).
   */
  enabled?: boolean;
}

const Graph2: React.FC<Graph2Props> = ({ topicIds, style, className = "", hidden, enabled = true }) => {
  const interaction = useGraph2Interaction();
  // Visibility-aware: pause fetch když collapsed wrapper nebo off-screen.
  // Wrappery vrátí undefined pokud Graph2 mountovaný mimo (kompatibilita).
  const partialCtx = useOptionalPartialWrapper();
  const hidingCtx = useOptionalHidingWrapper();
  const inView = useInView(interaction.containerRef);
  const wrapperVisible = (partialCtx?.visible ?? true) && (hidingCtx?.visible ?? true);
  const effectiveEnabled = enabled && inView && wrapperVisible;

  const data = useGraph2Data(
    topicIds,
    interaction.bounds,
    Math.max(100, interaction.containerWidth),
    effectiveEnabled
  );

  // Auto-scroll JEN při live WS update (ne při historický data load).
  // Před fixem efekt reagoval na data.minMax.maxT, který se měnil při každém
  // tile fetch → bounds shift → další fetch → infinite loop. Teď bere latest
  // timestamp PŘÍMO z valuesStore.byTopic (živý WS feed, ne tile data).
  const byTopic = useValuesStore((s) => s.byTopic);
  const topicsKey = topicIds.join("|");
  const latestLiveTs = useMemo(() => {
    let max = 0;
    for (const topicId of topicIds) {
      const v = byTopic.get(topicId);
      if (v && v.timestamp.getTime() > max) max = v.timestamp.getTime();
    }
    return max;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [byTopic, topicsKey]);

  useEffect(() => {
    if (
      latestLiveTs > 0 &&
      !interaction.isUserInteracting &&
      latestLiveTs > interaction.bounds.max
    ) {
      interaction.setBounds({ min: latestLiveTs - DEFAULT_BOUND, max: latestLiveTs });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestLiveTs, interaction.isUserInteracting]);

  // Spinner overlay POUZE když nemáme žádná data + běží fetch. Subsequent
  // reloady (pan/zoom) drží předchozí data → spinner zmizí, nová data
  // dotékají na pozadí bez vizuálního přerušení.
  const showInitialSpinner = data.loading && data.chartData.length === 0;

  return (
    <Graph2Render
      containerRef={interaction.containerRef}
      bounds={interaction.bounds}
      containerWidth={interaction.containerWidth}
      containerHeight={interaction.containerHeight}
      isPanning={interaction.isPanning}
      interactive={interaction.interactive}
      chartLock={interaction.chartLock}
      showTooltipAt={interaction.showTooltipAt}
      topicIds={topicIds}
      hidden={hidden}
      className={className}
      style={style}
      chartData={data.chartData}
      series={data.series}
      minMax={data.minMax}
      topics={data.topics}
      suspicious={data.suspicious}
      resolution={data.resolution}
      showInitialSpinner={showInitialSpinner}
    />
  );
};

export default React.memo(Graph2);
